/**
 * GAS WARS engine state, actions and events.
 *
 * Everything in GameState is a plain JSON-serializable value. No class
 * instances, no Maps, no Sets, no functions. That is what makes replays,
 * server authority and structuredClone-based reducers possible.
 */

import type {
  Ability,
  CardDefinition,
  CardType,
  Chain,
  Cost,
  Effect,
  Faction,
  Keyword,
  TokenDefinition,
} from './cardTypes';

export type { CardDefinition, TokenDefinition } from './cardTypes';

// ---------------------------------------------------------------------------
// Card database contract
// ---------------------------------------------------------------------------

/**
 * The engine never imports card JSON. It reads definitions through this
 * interface, which src/data registers at startup and tests register with
 * fixtures. See cardDb.ts.
 */
export interface CardDb {
  getCard(id: string): CardDefinition | undefined;
  getToken(id: string): TokenDefinition | undefined;
}

// ---------------------------------------------------------------------------
// Zones, phases, steps
// ---------------------------------------------------------------------------

export type Zone = 'deck' | 'hand' | 'battlefield' | 'dust' | 'blacklist' | 'stack' | 'limbo';

export const ZONES: Zone[] = ['deck', 'hand', 'battlefield', 'dust', 'blacklist', 'stack', 'limbo'];

export type Phase =
  | 'setup' // opening hands, redraw decisions
  | 'sync'
  | 'draw'
  | 'main1'
  | 'combat'
  | 'main2'
  | 'end'
  | 'over';

export const PHASE_ORDER: Phase[] = ['sync', 'draw', 'main1', 'combat', 'main2', 'end'];

export type CombatStep =
  | 'declareAttackers'
  | 'attackersDeclared'
  | 'declareBlockers'
  | 'blockersDeclared'
  | 'frontrunDamage'
  | 'regularDamage'
  | 'combatEnd';

export const COMBAT_STEP_ORDER: CombatStep[] = [
  'declareAttackers',
  'attackersDeclared',
  'declareBlockers',
  'blockersDeclared',
  'frontrunDamage',
  'regularDamage',
  'combatEnd',
];

// ---------------------------------------------------------------------------
// Gas
// ---------------------------------------------------------------------------

/** Gas pool. `generic` is gas that can only pay generic requirements. */
export interface GasPool {
  generic: number;
  btc: number;
  eth: number;
  sol: number;
  bnb: number;
  xrp: number;
}

export const CHAINS: Chain[] = ['btc', 'eth', 'sol', 'bnb', 'xrp'];

export function emptyGasPool(): GasPool {
  return { generic: 0, btc: 0, eth: 0, sol: 0, bnb: 0, xrp: 0 };
}

// ---------------------------------------------------------------------------
// Game objects
// ---------------------------------------------------------------------------

export interface TemporaryBuff {
  attack: number;
  health: number;
  /** 'turn' buffs expire in the End step. 'permanent' never expires. */
  duration: 'turn' | 'permanent';
  /** Turn number the buff was applied, for debugging and log rendering. */
  appliedTurn: number;
}

export interface KeywordGrant {
  keyword: Keyword;
  duration: 'turn' | 'permanent';
  appliedTurn: number;
}

/**
 * One physical card or token in the match. Every card in every deck gets an
 * instance at game creation, so instance ids are stable across zone moves.
 */
export interface GameObject {
  instanceId: string;
  /** Set for real cards. */
  cardId?: string;
  /** Set for tokens created by effects. Tokens never have cardId. */
  tokenId?: string;
  controller: string;
  owner: string;
  zone: Zone;
  exhausted: boolean;
  /** Frozen permanents skip readying in their controller's next Sync. */
  frozen: boolean;
  /** Marked combat and non-combat damage. Cleared in the End step. */
  damage: number;
  buffs: TemporaryBuff[];
  grantedKeywords: KeywordGrant[];
  removedKeywords: KeywordGrant[];
  /** Upgrades only: the host character's instanceId. */
  attachedTo?: string;
  /** Turn number this object last entered the battlefield. -1 while off it. */
  enteredTurn: number;
  /** Turn number this player's continuous control began, for attack legality. */
  controlSinceTurn: number;
  /** Generic counters, keyed by name. Unused in set one but part of the shape. */
  counters: Record<string, number>;
  /** X paid when the card was played, read by `{ kind: 'x' }` amounts. */
  xValue?: number;
  /** Combat: true while declared as an attacker. */
  attacking?: boolean;
  /** Combat: instanceId of the attacker this character is blocking. */
  blocking?: string;
  /** Keys of `oncePerTurn` abilities used this turn, as "ability:<index>". */
  oncePerTurnUsed: string[];
}

// ---------------------------------------------------------------------------
// Stack
// ---------------------------------------------------------------------------

/** A set of chosen target instance ids (or player ids) for one target slot. */
export type TargetSelection = string[];

export interface StackItem {
  id: string;
  kind: 'card' | 'ability' | 'trigger';
  controller: string;
  /** For 'card': the object that is on the stack. For ability/trigger: the source permanent. */
  sourceInstanceId: string;
  /** Index into the source card's abilities array, for ability/trigger items. */
  abilityIndex?: number;
  /** Snapshot of the effects to run, so later changes to the card cannot alter it. */
  effects: Effect[];
  /** Chosen targets, index-aligned with the item's choose-mode target slots. */
  targets: TargetSelection[];
  xValue?: number;
  /** The object that caused a trigger, for `mode: 'triggering'`. */
  triggeringInstanceId?: string;
  /** Upgrade cards only: the friendly character chosen as the host (RULES 11). */
  hostInstanceId?: string;
  /** Human-readable label for the log and the UI stack strip. */
  label: string;
}

// ---------------------------------------------------------------------------
// Combat
// ---------------------------------------------------------------------------

export interface CombatState {
  step: CombatStep;
  attackers: string[];
  /** attackerInstanceId -> blocker instance ids, in damage assignment order. */
  blockers: Record<string, string[]>;
  /** Attackers that already dealt damage in the Frontrun step. */
  dealtFrontrun: string[];
  /** Blockers that already dealt damage in the Frontrun step. */
  dealtFrontrunBlockers: string[];
}

export function emptyCombat(): CombatState {
  return {
    step: 'declareAttackers',
    attackers: [],
    blockers: {},
    dealtFrontrun: [],
    dealtFrontrunBlockers: [],
  };
}

// ---------------------------------------------------------------------------
// Pending choices
// ---------------------------------------------------------------------------

/** Where a choose-mode target lives inside an item's effect tree. */
export interface TargetSlotSpec {
  /** Path of indices through nested effects (conditional.then, sequence.effects, ...). */
  path: number[];
  /**
   * The card's shared slot name, when it gave one. Every effect with this name
   * uses this one selection, so a UI shows a single targeting prompt.
   */
  slot?: string;
  type: string;
  count: number;
  optional: boolean;
  /** Legal instance ids or player ids at the time the slot was opened. */
  legal: string[];
}

export type PendingChoice =
  | { kind: 'discard'; player: string; count: number }
  | { kind: 'scry'; player: string; cards: string[] }
  | { kind: 'chooseTargets'; player: string; stackItemId: string; slots: TargetSlotSpec[] }
  | { kind: 'orderBlockers'; player: string; attackerId: string; blockerIds: string[] }
  | { kind: 'sacrifice'; player: string; count: number; legal: string[] };

// ---------------------------------------------------------------------------
// Player
// ---------------------------------------------------------------------------

export type RedrawDecision = 'pending' | 'kept' | 'redrawn';

export interface PlayerState {
  id: string;
  name: string;
  integrity: number;
  /** Ordered instance ids. Index 0 is the top of the deck. */
  deck: string[];
  hand: string[];
  dust: string[];
  blacklist: string[];
  battlefield: {
    characters: string[];
    wallets: string[];
    protocols: string[];
    /** Upgrades are on the battlefield and also attachedTo a host. */
    upgrades: string[];
  };
  gas: GasPool;
  walletsInstalledThisTurn: number;
  redraw: RedrawDecision;
  /** Card types played this turn, for `playedCardThisTurn` conditions. */
  playedThisTurn: CardType[];
  conceded: boolean;
  lost: boolean;
  /** Set when the player already took their deck-exhaustion loss this draw. */
  deckExhaustionCount: number;
}

// ---------------------------------------------------------------------------
// Game state
// ---------------------------------------------------------------------------

export interface GameOptions {
  startingIntegrity: number;
  openingHandSize: number;
  redrawHandSize: number;
  handSizeLimit: number;
  /** Dev actions are rejected unless this is true. */
  devMode: boolean;
  /** Skip the redraw phase and go straight to turn 1 (used by the simulator). */
  skipRedraw: boolean;
  /** Fixed starting player id. When absent the seeded RNG decides. */
  startingPlayer?: string;
}

export function defaultOptions(): GameOptions {
  return {
    startingIntegrity: 20,
    openingHandSize: 7,
    redrawHandSize: 6,
    handSizeLimit: 8,
    devMode: false,
    skipRedraw: false,
  };
}

export interface GameState {
  /** Bumped when the state shape changes, so stored replays can be migrated. */
  version: number;
  seed: number;
  rngState: number;
  options: GameOptions;
  players: Record<string, PlayerState>;
  /** Stable seating order. Index 0 went first. */
  playerOrder: string[];
  objects: Record<string, GameObject>;
  /** Monotonic counter for instance and stack item ids. */
  nextId: number;
  turn: number;
  activePlayer: string;
  priorityPlayer: string;
  phase: Phase;
  combat: CombatState;
  stack: StackItem[];
  /** Consecutive passes with a non-empty or empty stack, for stack resolution. */
  passCount: number;
  /**
   * True while a priority window opened by NEXT PHASE is waiting for the
   * opponent. When they pass with an empty stack, the phase actually advances.
   */
  pendingAdvance: boolean;
  pendingChoices: PendingChoice[];
  /** Triggers waiting to go on the stack the next time a player gets priority. */
  pendingTriggers: StackItem[];
  winner: string | null;
  draw: boolean;
  /** Full event history. Rendered by log.ts. */
  eventLog: GameEvent[];
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export interface BlockAssignment {
  blocker: string;
  attacker: string;
}

export type Action =
  // setup
  | { type: 'Redraw'; player: string }
  | { type: 'KeepHand'; player: string }
  // main phase
  | { type: 'InstallWallet'; player: string; cardInstanceId: string }
  | {
      type: 'PlayCard';
      player: string;
      cardInstanceId: string;
      targets?: TargetSelection[];
      xValue?: number;
    }
  | {
      type: 'ActivateAbility';
      player: string;
      instanceId: string;
      abilityIndex: number;
      targets?: TargetSelection[];
      /** Instance ids to sacrifice or discard when the cost requires it. */
      costChoices?: string[];
    }
  // combat
  | { type: 'DeclareAttackers'; player: string; attackers: string[] }
  | { type: 'DeclareBlockers'; player: string; assignments: BlockAssignment[] }
  | { type: 'OrderBlockers'; player: string; attacker: string; order: string[] }
  // flow
  | { type: 'Pass'; player: string }
  | { type: 'NextPhase'; player: string }
  | { type: 'Concede'; player: string }
  // choices
  | { type: 'ChooseTargets'; player: string; stackItemId: string; targets: TargetSelection[] }
  | { type: 'Discard'; player: string; cardInstanceIds: string[] }
  | { type: 'Scry'; player: string; toBottom: string[] }
  | { type: 'Sacrifice'; player: string; cardInstanceIds: string[] }
  // dev actions, rejected unless options.devMode
  | { type: 'DevGiveCard'; player: string; cardId: string }
  | { type: 'DevPutIntoPlay'; player: string; cardId?: string; tokenId?: string }
  | { type: 'DevSetGas'; player: string; gas: Partial<GasPool> }
  | { type: 'DevSetIntegrity'; player: string; integrity: number }
  | { type: 'DevSetTurn'; turn: number }
  | { type: 'DevDraw'; player: string; amount: number }
  | { type: 'DevShuffle'; player: string }
  | { type: 'DevCreateToken'; player: string; tokenId: string; amount?: number }
  | { type: 'DevDestroy'; instanceId: string }
  | { type: 'DevClearBoard' }
  | { type: 'DevSkipPhase' }
  | { type: 'DevSetDeckOrder'; player: string; instanceIds: string[] };

export type ActionType = Action['type'];

export const DEV_ACTION_TYPES: ActionType[] = [
  'DevGiveCard',
  'DevPutIntoPlay',
  'DevSetGas',
  'DevSetIntegrity',
  'DevSetTurn',
  'DevDraw',
  'DevShuffle',
  'DevCreateToken',
  'DevDestroy',
  'DevClearBoard',
  'DevSkipPhase',
  'DevSetDeckOrder',
];

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

export type GameEvent =
  | { type: 'GameCreated'; turn: number; seed: number; startingPlayer: string }
  | { type: 'HandDrawn'; turn: number; player: string; count: number }
  | { type: 'HandRedrawn'; turn: number; player: string; count: number }
  | { type: 'HandKept'; turn: number; player: string }
  | { type: 'TurnStarted'; turn: number; player: string }
  | { type: 'PhaseChanged'; turn: number; player: string; phase: Phase }
  | { type: 'CombatStepChanged'; turn: number; player: string; step: CombatStep }
  | { type: 'CardDrawn'; turn: number; player: string; instanceId: string; cardName: string }
  | { type: 'DeckExhausted'; turn: number; player: string; amount: number }
  | { type: 'CardsSynced'; turn: number; player: string; instanceIds: string[] }
  | { type: 'Unfroze'; turn: number; player: string; instanceId: string; name: string }
  | { type: 'GasGenerated'; turn: number; player: string; chain: Chain | 'generic'; amount: number }
  | { type: 'GasSpent'; turn: number; player: string; cost: Cost; cardName: string }
  | { type: 'GasPoolEmptied'; turn: number; player: string; lost: number }
  | { type: 'WalletInstalled'; turn: number; player: string; instanceId: string; name: string }
  | {
      type: 'CardPlayed';
      turn: number;
      player: string;
      instanceId: string;
      name: string;
      cardType: CardType;
    }
  | { type: 'AbilityActivated'; turn: number; player: string; instanceId: string; name: string; label: string }
  | { type: 'TriggerFired'; turn: number; player: string; instanceId: string; name: string; event: string }
  | { type: 'StackItemResolved'; turn: number; player: string; stackItemId: string; label: string }
  | { type: 'Reverted'; turn: number; player: string; label: string }
  | { type: 'Fizzled'; turn: number; player: string; label: string }
  | { type: 'Deployed'; turn: number; player: string; instanceId: string; name: string; cardType: CardType }
  | { type: 'TokenCreated'; turn: number; player: string; instanceId: string; name: string }
  | { type: 'TokenCeased'; turn: number; player: string; instanceId: string; name: string }
  | { type: 'UpgradeAttached'; turn: number; player: string; instanceId: string; name: string; hostName: string }
  | { type: 'Destroyed'; turn: number; player: string; instanceId: string; name: string; reason: string }
  | { type: 'Died'; turn: number; player: string; instanceId: string; name: string }
  | { type: 'Sacrificed'; turn: number; player: string; instanceId: string; name: string }
  | { type: 'Bounced'; turn: number; player: string; instanceId: string; name: string }
  | { type: 'Blacklisted'; turn: number; player: string; instanceId: string; name: string }
  | { type: 'Recovered'; turn: number; player: string; instanceId: string; name: string; to: 'hand' | 'battlefield' }
  | { type: 'Discarded'; turn: number; player: string; instanceId: string; name: string }
  | { type: 'Milled'; turn: number; player: string; count: number }
  | { type: 'Scried'; turn: number; player: string; look: number; toBottom: number }
  | { type: 'WalletFetched'; turn: number; player: string; instanceId: string | null; name: string }
  | {
      type: 'DamageDealt';
      turn: number;
      player: string;
      sourceInstanceId: string | null;
      sourceName: string;
      targetInstanceId: string;
      targetName: string;
      amount: number;
    }
  | {
      type: 'DamageDealtToPlayer';
      turn: number;
      player: string;
      sourceInstanceId: string | null;
      sourceName: string;
      targetPlayer: string;
      amount: number;
    }
  | { type: 'IntegrityChanged'; turn: number; player: string; delta: number; total: number; reason: string }
  | { type: 'VampRestored'; turn: number; player: string; sourceName: string; amount: number }
  | { type: 'Buffed'; turn: number; player: string; instanceId: string; name: string; attack: number; health: number; duration: string }
  | { type: 'KeywordGranted'; turn: number; player: string; instanceId: string; name: string; keyword: Keyword }
  | { type: 'KeywordRemoved'; turn: number; player: string; instanceId: string; name: string; keyword: Keyword }
  | { type: 'Exhausted'; turn: number; player: string; instanceId: string; name: string }
  | { type: 'Readied'; turn: number; player: string; instanceId: string; name: string }
  | { type: 'Frozen'; turn: number; player: string; instanceId: string; name: string }
  | { type: 'AttackersDeclared'; turn: number; player: string; attackers: { instanceId: string; name: string }[] }
  | {
      type: 'BlockersDeclared';
      turn: number;
      player: string;
      blocks: { blocker: string; blockerName: string; attacker: string; attackerName: string }[];
    }
  | { type: 'LegendaryRuleApplied'; turn: number; player: string; instanceId: string; name: string }
  | { type: 'HandSizeDiscard'; turn: number; player: string; count: number }
  | { type: 'EffectsExpired'; turn: number; player: string; count: number }
  | { type: 'PriorityChanged'; turn: number; player: string }
  | { type: 'Passed'; turn: number; player: string }
  | { type: 'Conceded'; turn: number; player: string }
  | { type: 'PlayerLost'; turn: number; player: string; reason: string }
  | { type: 'GameEnded'; turn: number; player: string; winner: string | null; draw: boolean }
  | { type: 'DevAction'; turn: number; player: string; detail: string };

export type GameEventType = GameEvent['type'];

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export type RulesErrorCode =
  | 'NOT_YOUR_TURN'
  | 'WRONG_PHASE'
  | 'STACK_NOT_EMPTY'
  | 'NOT_ENOUGH_GAS'
  | 'WALLET_ALREADY_INSTALLED'
  | 'NO_LEGAL_TARGET'
  | 'ILLEGAL_TARGET'
  | 'CANT_ATTACK'
  | 'CANT_BLOCK'
  | 'NOT_READY'
  | 'NO_PRIORITY'
  | 'ONCE_PER_TURN_USED'
  | 'DECK_INVALID'
  /** Not in RULES.md section 13, used only for malformed input and dev guards. */
  | 'INVALID_ACTION';

export const RULES_ERROR_CODES: RulesErrorCode[] = [
  'NOT_YOUR_TURN',
  'WRONG_PHASE',
  'STACK_NOT_EMPTY',
  'NOT_ENOUGH_GAS',
  'WALLET_ALREADY_INSTALLED',
  'NO_LEGAL_TARGET',
  'ILLEGAL_TARGET',
  'CANT_ATTACK',
  'CANT_BLOCK',
  'NOT_READY',
  'NO_PRIORITY',
  'ONCE_PER_TURN_USED',
  'DECK_INVALID',
  'INVALID_ACTION',
];

export class RulesError extends Error {
  readonly code: RulesErrorCode;
  /** The same sentence the UI shows the player. */
  readonly reason: string;

  constructor(code: RulesErrorCode, reason: string) {
    super(`${code}: ${reason}`);
    this.name = 'RulesError';
    this.code = code;
    this.reason = reason;
  }
}

// ---------------------------------------------------------------------------
// Derived / computed shapes
// ---------------------------------------------------------------------------

/** Result of layering base stats, upgrades, statics and temporary buffs. */
export interface EffectiveStats {
  attack: number;
  health: number;
  keywords: Keyword[];
  /** true when a static says this character cannot block. */
  cantBlock: boolean;
  /** Extra combat damage from `damageModifier` statics. */
  damageBonus: number;
}

/** A redacted view: same shape as GameState, with hidden zones replaced. */
export interface RedactedGameState extends Omit<GameState, 'players' | 'objects'> {
  players: Record<string, RedactedPlayerState>;
  objects: Record<string, GameObject>;
  /** The viewer this state was redacted for. */
  viewer: string;
}

export interface RedactedPlayerState extends Omit<PlayerState, 'deck' | 'hand'> {
  /** Hidden for the opponent: contents replaced by an empty array. */
  deck: string[];
  deckCount: number;
  hand: string[];
  handCount: number;
}

/** What a UI or bot needs to render one ability button. */
export interface AbilityRef {
  instanceId: string;
  abilityIndex: number;
  ability: Ability;
}

export interface CardIdentity {
  name: string;
  faction: Faction;
  type: CardType;
  cost: Cost;
}
